import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";

export const InteractiveLogo = () => {
  const { scrollY } = useScroll();

  const smoothScroll = useSpring(scrollY, { stiffness: 120, damping: 30, mass: 0.2 });
  const scrollRotateX = useTransform(smoothScroll, [0, 1200], [10, -10], { clamp: true });
  const scrollRotateY = useTransform(smoothScroll, [0, 1200], [-18, 18], { clamp: true });
  const scale = useSpring(useTransform(smoothScroll, [0, 1200], [1, 1.08], { clamp: true }), {
    stiffness: 120,
    damping: 30,
    mass: 0.2,
  });

  const baseRotateX = useMotionValue(12);
  const baseRotateY = useMotionValue(-24);

  useAnimationFrame((time, delta) => {
    const nextRotateY = (baseRotateY.get() + delta * 0.01) % 360;
    baseRotateY.set(nextRotateY);
    baseRotateX.set(12 + Math.sin(time / 2500) * 4);
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  const rotateX = useTransform(
    [baseRotateX, scrollRotateX],
    // @ts-ignore
    ([base, scroll]) => base + scroll
  );
  const rotateY = useTransform(
    [baseRotateY, scrollRotateY],
    // @ts-ignore
    ([base, scroll]) => base + scroll
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <div className="w-[300px] h-[300px] [perspective:1200px] relative flex items-center justify-center pointer-events-none">
      <motion.div
        style={{
          rotateX,
          rotateY,
          scale,
        }}
        className="relative w-32 h-32 [transform-style:preserve-3d] transform-gpu will-change-transform"
      >
        {/* Outer Tech Cube */}
        <CubeFace
          transform="translateZ(64px)"
          className="border border-white/20 bg-white/5"
        />
        <CubeFace
          transform="rotateY(180deg) translateZ(64px)"
          className="border border-white/20 bg-white/5"
        />
        <CubeFace
          transform="rotateY(90deg) translateZ(64px)"
          className="border border-white/20 bg-white/5"
        />
        <CubeFace
          transform="rotateY(-90deg) translateZ(64px)"
          className="border border-white/20 bg-white/5"
        />
        <CubeFace
          transform="rotateX(90deg) translateZ(64px)"
          className="border border-white/20 bg-white/5"
        />
        <CubeFace
          transform="rotateX(-90deg) translateZ(64px)"
          className="border border-white/20 bg-white/5"
        />

        {/* Inner Core */}
        <div className="absolute inset-0 flex items-center justify-center [transform-style:preserve-3d]">
             <motion.div 
                animate={{ rotateY: 360, rotateZ: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 relative [transform-style:preserve-3d]"
             >
                 <CubeFace transform="translateZ(32px)" className="bg-sapphire-600/80 border border-white/40 shadow-[0_0_20px_rgba(37,99,235,0.3)]" />
                 <CubeFace transform="rotateY(180deg) translateZ(32px)" className="bg-indigo-500/60 border border-white/30" />
                 <CubeFace transform="rotateY(90deg) translateZ(32px)" className="bg-indigo-500/60 border border-white/30" />
                 <CubeFace transform="rotateY(-90deg) translateZ(32px)" className="bg-indigo-500/60 border border-white/30" />
                 <CubeFace transform="rotateX(90deg) translateZ(32px)" className="bg-indigo-500/60 border border-white/30" />
                 <CubeFace transform="rotateX(-90deg) translateZ(32px)" className="bg-indigo-500/60 border border-white/30" />
             </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

const CubeFace = ({ transform, className }: { transform: string; className?: string }) => (
  <div
    className={`absolute inset-0 flex items-center justify-center backface-visible ${className}`}
    style={{ transform }}
  >
    <div className="w-0.5 h-0.5 bg-white/20 rounded-full" />
  </div>
);
